import React, { useState, useRef, useEffect } from 'react';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef({});

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
      window.scrollTo(0, 0);
    }, []); 

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'personal-info', title: '1. Personal Identification Information' },
    { id: 'info-collection', title: '2. Information Collection' },
    { id: 'data-collected', title: '3. Data We Collect' },
    { id: 'location-info', title: '4. Location Information' },
    { id: 'info-usage', title: '5. How We Use This Information' },
    { id: 'usage', title: '6. Information Usage' },
    { id: 'protection', title: '7. How We Protect Your Information' },
    { id: 'sharing', title: '8. Data Sharing and Disclosure' },
    { id: 'responsibilities', title: '9. User Responsibilities' },
    { id: 'providers', title: '10. Service Providers' },
    { id: 'links', title: '11. Links to Other Sites' },
    { id: 'trust', title: '12. Trust and Safety' },
    { id: 'changes', title: '13. Changes to This Privacy Policy' },
    { id: 'rights', title: '14. Your Data Protection Rights' },
    { id: 'contact', title: '15. Contact Us' }
  ];

  return (
    <div className="max-w-6xl mx-auto p-4 bg-white">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-blue-800 mb-2">Privacy Policy</h1>
        <p className="text-xl text-gray-600">IndiaHirings.com</p>
        <p className="text-gray-500 mt-2">Effective Date: March 18, 2025</p>
      </div>

      {/* Introduction */}
      <div className="bg-blue-50 p-6 rounded-lg shadow-sm mb-8">
        <p className="text-gray-800 leading-relaxed">
          IndiaHirings.com ("we," "our," or "us") is committed to protecting your privacy and ensuring a safe online experience. This Privacy Policy describes how we collect, use, store, and protect your personal information when you use our website and mobile application (collectively referred to as the "Platform"). By accessing and using the Platform, you agree to the practices outlined in this Privacy Policy.
        </p>
        <p className="text-gray-800 leading-relaxed mt-4 font-medium">
          Please read this Privacy Policy carefully before using our services.
        </p>
      </div>

      {/* Table of Contents */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-blue-800 mb-4">Table of Contents</h2>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {sections.slice(1).map(section => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`text-left p-2 rounded-md transition-colors hover:bg-blue-50 ${
                  activeSection === section.id ? 'bg-blue-100 text-blue-800' : 'text-gray-700'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-10">
        {/* Section 1 */}
        <div 
          ref={el => sectionRefs.current['personal-info'] = el}
          id="personal-info"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">1. Personal Identification Information</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              We collect personal identification information ("Personal Information") when users visit our website, register, fill out forms, apply for jobs, or interact with our Platform in other ways. The types of Personal Information we collect may include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Contact Details:</span> Name, email address, phone number.</li>
              <li><span className="font-medium">Job Preferences:</span> Desired job title, preferred locations, salary expectations.</li>
              <li><span className="font-medium">Resume and Professional Information:</span> Work experience, skills, education, certifications, portfolio, etc.</li>
              <li><span className="font-medium">Account Information:</span> Username, password, and other account-related information.</li>
              <li><span className="font-medium">Payment Information:</span> If applicable, billing details for any paid services on the Platform.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              This information helps us personalize your experience and recommend the most relevant job opportunities.
            </p>
          </div>
        </div>

        {/* Section 2 */}
        <div 
          ref={el => sectionRefs.current['info-collection'] = el}
          id="info-collection"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">2. Information Collection</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              We collect both Personal Information and Non-Personal Information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><span className="font-medium">Personal Information:</span> Data that directly identifies you as an individual.</li>
              <li><span className="font-medium">Non-Personal Information:</span> Data that cannot be used to identify you personally, such as demographic information (age, gender) and aggregated usage data (e.g., number of users accessing specific pages).</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Information may be collected in the following ways:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Account Registration:</span> When you create an account on the Platform, we gather information such as your name, email address, phone number, and password.</li>
              <li><span className="font-medium">Profile Data:</span> When you create or update your profile, we may collect your educational background, work experience, skills, and job preferences.</li>
              <li><span className="font-medium">Job Applications:</span> When you apply for jobs, we collect the application details you provide, such as your resume and cover letter.</li>
              <li><span className="font-medium">Usage Data:</span> We automatically collect information about your interaction with the Platform, such as your IP address, browser type, device type, pages visited, and time spent on the Platform.</li>
              <li><span className="font-medium">Cookies and Tracking Technologies:</span> We use cookies, web beacons, and similar technologies to collect data about how you use our Platform. This data helps us improve user experience.</li>
            </ul>
          </div>
        </div>

        {/* Section 3 */}
        <div 
          ref={el => sectionRefs.current['data-collected'] = el}
          id="data-collected"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">3. Data We Collect</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              The data we collect may include, but is not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Personal Information:</span> Name, contact details (email, phone number), job preferences, resume/CV, work experience, education, etc.</li>
              <li><span className="font-medium">Device Information:</span> Device type, browser type, operating system, IP address, and location data.</li>
              <li><span className="font-medium">Cookies:</span> Data related to your preferences, device, and interactions with our website.</li>
              <li><span className="font-medium">Location Information:</span> With your consent, we may collect location data to provide job recommendations based on your geographical area.</li>
            </ul>
          </div>
        </div>

        {/* Section 4 */}
        <div 
          ref={el => sectionRefs.current['location-info'] = el}
          id="location-info"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">4. Location Information</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700">
              With your permission, we may collect information about your location to recommend jobs or employers in your region. You may be asked to enable location access on our mobile app or website for more accurate location-based services. You can disable location tracking at any time through your device or browser settings.
            </p>
          </div>
        </div>

        {/* Section 5 */}
        <div 
          ref={el => sectionRefs.current['info-usage'] = el}
          id="info-usage"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">5. How We Use This Information</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              We use your personal information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Job Matching and Recommendations:</span> To recommend jobs based on your profile, skills, experience, and preferences.</li>
              <li><span className="font-medium">Account Management:</span> To allow you to create, update, and manage your job seeker or employer profile.</li>
              <li><span className="font-medium">Job Applications:</span> To process your job applications and send you related notifications.</li>
              <li><span className="font-medium">Communication:</span> To send you job alerts, newsletters, updates, and promotional content (only if you have opted-in).</li>
              <li><span className="font-medium">Analytics and Improvements:</span> To analyze usage data and improve our services, functionality, and user experience.</li>
              <li><span className="font-medium">Customer Support:</span> To respond to your queries, complaints, or technical issues.</li>
              <li><span className="font-medium">Legal Obligations:</span> To comply with legal requirements, prevent fraud, and protect our rights.</li>
            </ul>
          </div>
        </div>

        {/* Section 6 */}
        <div 
          ref={el => sectionRefs.current['usage'] = el}
          id="usage"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">6. Information Usage</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              The information we collect is used in the following ways:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Personalizing Your Experience:</span> To tailor job recommendations, content, and features based on your profile.</li>
              <li><span className="font-medium">Communicating with You:</span> To send you notifications about your job applications, job alerts, system updates, and any issues related to your account.</li>
              <li><span className="font-medium">Improving Platform Performance:</span> To monitor and analyze trends in user behavior, identify areas for improvement, and enhance the user experience.</li>
              <li><span className="font-medium">Legal Compliance:</span> To meet legal obligations, comply with regulatory requirements, or defend against legal claims.</li>
            </ul>
          </div>
        </div>

        {/* Section 7 */}
        <div 
          ref={el => sectionRefs.current['protection'] = el}
          id="protection"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">7. How We Protect Your Information</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              We implement a variety of security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><span className="font-medium">Encryption:</span> All sensitive personal data (such as financial information) is transmitted over secure protocols (SSL/TLS) and stored in encrypted form.</li>
              <li><span className="font-medium">Access Control:</span> We limit access to your personal data to authorized personnel only.</li>
              <li><span className="font-medium">Secure Servers:</span> Our Platform is hosted on secure servers protected by firewalls and other security measures to prevent unauthorized access.</li>
              <li><span className="font-medium">Regular Audits:</span> We conduct regular audits of our security practices to identify and mitigate vulnerabilities.</li>
            </ul>
            <p className="text-gray-700">
              Despite these security measures, no method of data transmission or storage is completely secure. We cannot guarantee the absolute security of your data.
            </p>
          </div>
        </div>

        {/* Section 8 */}
        <div 
          ref={el => sectionRefs.current['sharing'] = el}
          id="sharing"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">8. Data Sharing and Disclosure</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              We may share your information in the following cases:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">With Employers:</span> If you apply for jobs, your personal information (such as resume and contact details) will be shared with the respective employer.</li>
              <li><span className="font-medium">With Service Providers:</span> We may share your data with third-party service providers who assist us with operations such as website hosting, payment processing, email communication, and customer support.</li>
              <li><span className="font-medium">For Legal Compliance:</span> We may disclose your personal data to comply with applicable laws, legal processes, or government requests.</li>
              <li><span className="font-medium">In the Event of a Sale or Merger:</span> If our company is sold, merged, or acquired, your personal data may be transferred as part of the transaction.</li>
            </ul>
          </div>
        </div>

        {/* Continue with the remaining sections */}
        {/* Section 9 */}
        <div 
          ref={el => sectionRefs.current['responsibilities'] = el}
          id="responsibilities"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">9. User Responsibilities</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              As a user, you are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Providing Accurate Information:</span> Ensuring the information you provide to us is true, accurate, and up-to-date.</li>
              <li><span className="font-medium">Account Security:</span> Protecting your account details, including your password, and preventing unauthorized access.</li>
              <li><span className="font-medium">Compliance with Terms:</span> Using the Platform in compliance with our Terms of Service and applicable laws.</li>
              <li><span className="font-medium">Reviewing Communications:</span> Regularly checking your email and notifications for updates regarding your job applications and other Platform activities.</li>
            </ul>
          </div>
        </div>

        {/* Section 10 */}
        <div 
          ref={el => sectionRefs.current['providers'] = el}
          id="providers"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">10. Service Providers</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              We may use third-party service providers to help us operate the Platform, such as:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Cloud Hosting Services</li>
              <li>Payment Processors</li>
              <li>Customer Support Tools</li>
              <li>Marketing and Analytics Providers</li>
            </ul>
            <p className="text-gray-700 mt-4">
              These third-party providers may have access to your personal information, but only to the extent necessary to perform their services. They are required not to disclose or use your data for other purposes.
            </p>
          </div>
        </div>

        {/* Section 11 */}
        <div 
          ref={el => sectionRefs.current['links'] = el}
          id="links"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">11. Links to Other Sites</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700">
              Our Platform may contain links to third-party websites, services, or products that are not operated by us. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to review the privacy policies of any third-party websites you visit.
            </p>
          </div>
        </div>

        {/* Section 12 */}
        <div 
          ref={el => sectionRefs.current['trust'] = el}
          id="trust"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">12. Trust and Safety</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              We prioritize your trust and safety while using the Platform. To protect you, we:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Monitor for Fraudulent Activity:</span> We actively monitor and detect any fraudulent activity to ensure a secure environment.</li>
              <li><span className="font-medium">Report Suspicious Behaviour:</span> If you encounter any suspicious behavior, please report it immediately to us via our support channels.</li>
              <li><span className="font-medium">Verify Employers:</span> We take steps to verify the legitimacy of employers and job listings posted on the Platform.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              However, we cannot guarantee 100% protection against fraud or abuse. Always exercise caution when sharing personal information.
            </p>
          </div>
        </div>

        {/* Section 13 */}
        <div 
          ref={el => sectionRefs.current['changes'] = el}
          id="changes"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">13. Changes to This Privacy Policy</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700">
              We reserve the right to update or modify this Privacy Policy at any time. Any changes will be posted on this page with an updated "Effective Date." We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
            </p>
          </div>
        </div>

        {/* Section 14 */}
        <div 
          ref={el => sectionRefs.current['rights'] = el}
          id="rights"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">14. Your Data Protection Rights</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><span className="font-medium">Right to Access:</span> You have the right to request a copy of your personal data held by us.</li>
              <li><span className="font-medium">Right to Rectification:</span> You can correct any inaccurate or incomplete information we have about you.</li>
              <li><span className="font-medium">Right to Erasure:</span> You may request the deletion of your account and personal data.</li>
              <li><span className="font-medium">Right to Restriction:</span> You can request the restriction of processing your personal data in certain cases.</li>
              <li><span className="font-medium">Right to Portability:</span> You may request a copy of your personal data in a structured, machine-readable format.</li>
              <li><span className="font-medium">Right to Object:</span> You have the right to object to the processing of your data, including for marketing purposes.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, please contact us at legal@indiaHirings.com.
            </p>
          </div>
        </div>

        {/* Section 15 */}
        <div 
          ref={el => sectionRefs.current['contact'] = el}
          id="contact"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">15. Contact Us</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or how your personal data is being processed, please contact us at:
            </p>
            <div className="bg-blue-50 p-4 rounded-md">
              <p className="font-medium text-blue-800">IndiaHirings.com</p>
              <p className="text-blue-800">legal@indiaHirings.com</p>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <div className="fixed bottom-6 right-6">
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PrivacyPolicy;